import { FC, useState } from "react";
import { Box, Container, Tooltip, useMediaQuery } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { map, some } from "lodash";

import {
  Button,
  ConnectAccountErrorMessage,
  Modal,
  StatusMessage,
} from "components";
import { CredentialsForm } from "containers";
import { FormValues } from "containers/CredentialsForm";
import {
  removeAssetKYWCredentialSubmission,
  removeAssetKYWRoyaltyAccount,
} from "services/assets";
import { RoyaltyPayor } from "services/royaltyPayor/types";
import { useValidateRoyaltyAccountCredentials } from "services/validateRoyaltyAccountCredentialsTasks/validateCredentials";
import theme from "theme";

interface EditRoyaltyAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssetEdited: () => void;
  assetIds: number[];
  hasDeal: boolean;
  username: string;
  royaltyPayor: RoyaltyPayor;
  handleUpdatePassword: (values: FormValues) => Promise<void>;
}

const EditRoyaltyAccountModal: FC<EditRoyaltyAccountModalProps> = ({
  isOpen,
  onClose,
  onAssetEdited,
  assetIds,
  hasDeal,
  username,
  royaltyPayor,
  handleUpdatePassword,
}) => {
  const [editStage, setEditStage] = useState<
    "editing" | "removing" | "removed" | "edited"
  >("editing");
  const {
    isOnTwoFAStep,
    validateCredentials,
    validateCredentialsErrorInfo,
  } = useValidateRoyaltyAccountCredentials();
  const [updatePasswordErrorInfo, setUpdatePasswordErrorInfo] = useState(null);
  const [removeAccountError, setRemoveAccountError] = useState(null);

  const title =
    editStage === "editing"
      ? "Edit Royalty Account"
      : editStage === "removing"
      ? "Remove Royalty Account?"
      : editStage === "removed"
      ? "Royalty Account Removed"
      : "Royalty Account Edited";

  const handleValidate = async (values: FormValues) => {
    if (some(royaltyPayor.permissions, { codename: "get_credentials" })) {
      handleSuccessfulSubmit(values);
      return;
    }
    const success = await validateCredentials({
      username: values.username,
      password: values.password,
      royalty_payor: royaltyPayor.name,
      two_fa_code: values.twoFACode || "",
    });

    if (success) {
      handleSuccessfulSubmit(values);
    }
  };

  const handleSuccessfulSubmit = async (values: FormValues) => {
    setUpdatePasswordErrorInfo(null);
    try {
      if (some(royaltyPayor.permissions, { codename: "get_valuation" })) {
        await handleUpdatePassword(values);
      } else {
        await handleUpdatePassword(values);
        setEditStage("edited");
      }
    } catch (e) {
      setUpdatePasswordErrorInfo({
        errorType: "other",
        message: "Failed to edit royalty account.",
      });
    }
  };

  const handleRemoveAssets = async (assetIds: number[]) => {
    try {
      await Promise.all(
        map(assetIds, async (id: number) => {
          if (some(royaltyPayor.permissions, { codename: "get_credentials" })) {
            await removeAssetKYWCredentialSubmission(id);
          } else {
            await removeAssetKYWRoyaltyAccount(id);
          }
          onAssetEdited();
        }),
      );
      setEditStage("removed");
    } catch (error) {
      setRemoveAccountError(
        "This Royalty Account has a listing or deal and it can not be removed.",
      );
    }
  };

  const handleClose = () => {
    if (editStage === "removed") {
      onAssetEdited();
    }
    setEditStage("editing");
    setUpdatePasswordErrorInfo(null);
    setRemoveAccountError(null);
    onClose();
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Modal open={isOpen} onClose={handleClose} title={title}>
      {/* maxWidth="md" gives 444px max-width
       setting `fixed` + width: "380px" (380 = (444px - 2x(32px)) -> 32px is side padding)
       results in constant width of 444px */}
      <Container
        maxWidth="md"
        fixed
        style={isMobile ? undefined : { width: "380px" }}
        disableGutters
      >
        {(!!validateCredentialsErrorInfo || !!updatePasswordErrorInfo) && (
          <Alert severity="warning" icon={false}>
            {validateCredentialsErrorInfo ? (
              <ConnectAccountErrorMessage
                errorInfo={validateCredentialsErrorInfo}
                royaltyPayor={royaltyPayor}
              />
            ) : (
              updatePasswordErrorInfo?.message
            )}
          </Alert>
        )}
        {removeAccountError && (
          <Alert severity="warning" icon={false}>
            {removeAccountError}
          </Alert>
        )}
        {editStage === "editing" ? (
          <>
            <CredentialsForm
              royaltyPayor={royaltyPayor}
              onSubmit={handleValidate}
              isOnTwoFAStep={isOnTwoFAStep}
              initialValues={{
                username: username,
                password: "",
                twoFACode: "",
              }}
              usernameProps={{
                disabled: true,
              }}
              passwordProps={{
                placeholder: "••••••••••••••",
                autoFocus: true,
              }}
              showDataDisclosure={false}
              submitButtonProps={{ fullWidth: true }}
              submitButtonText="Save"
            />
            <Box mt={2}>
              <Tooltip
                title={
                  hasDeal
                    ? "There is a deal on this royalty account, it can not be removed."
                    : ""
                }
                placement="top"
              >
                <Box>
                  <Button
                    fullWidth
                    customColor="red"
                    variant="outlined"
                    onClick={() => setEditStage("removing")}
                    disabled={hasDeal}
                  >
                    Remove Royalty Account
                  </Button>
                </Box>
              </Tooltip>
            </Box>
          </>
        ) : editStage === "removing" ? (
          <>
            <StatusMessage
              type="failureHollow"
              message="Are you sure you want to remove this royalty account? You will not be able to see analysis and offers once it is removed."
            />
            <Button
              variant="outlined"
              customColor="red"
              onClick={() => handleRemoveAssets(assetIds)}
              fullWidth
              disabled={!!removeAccountError}
            >
              Yes, Remove This Account
            </Button>
            <Button
              customColor="green"
              variant="contained"
              onClick={() => {
                setUpdatePasswordErrorInfo(null);
                setRemoveAccountError(null);
                setEditStage("editing");
              }}
              fullWidth
              style={{ marginTop: theme.spacing(2) }}
            >
              Nevermind
            </Button>
          </>
        ) : editStage === "removed" ? (
          <StatusMessage
            type="failure"
            message="Account successfully removed."
          />
        ) : (
          <StatusMessage
            type="success"
            message={`${royaltyPayor.display_name} account successfully edited.`}
          />
        )}
      </Container>
    </Modal>
  );
};

export default EditRoyaltyAccountModal;
